import { useState, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";

const FilterableDropdown = ({
  label,
  value,
  options = [],
  onChange,
  isLabelNeeded = true,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const selectedOption = options.find((opt) => opt.value === value)?.label;

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex items-start gap-4 flex-wrap relative" ref={dropdownRef}>
      <div className="relative">
        {/* Floating Label */}
        {isLabelNeeded && (
          <label
            htmlFor="filterable-dropdown"
            className="absolute -top-3.5 left-3 z-10 px-1 text-xs font-medium text-muted-foreground bg-background"
          >
            {label}
          </label>
        )}

        {/* Dropdown Trigger Button */}
        <button
          id="filterable-dropdown"
          type="button"
          onClick={() => setShowDropdown((prev) => !prev)}
          className="min-w-[240px] justify-between rounded-md border border-input bg-background text-foreground text-sm px-4 py-2.5 flex items-center hover:bg-muted/50 transition-all"
        >
          <span>
            {selectedOption || (
              <span className="text-muted-foreground">Select {label}</span>
            )}
          </span>
          <ChevronDown
            className={`w-4 h-4 ml-2 transition-transform duration-200 ${
              showDropdown ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Dropdown Panel */}
        {showDropdown && (
          <div className="absolute left-0 mt-1 min-w-[240px] max-h-[300px] overflow-y-auto rounded-md border border-input bg-popover shadow-md z-50">
            {/* 🔍 Search Input */}
            <div className="flex items-center px-2 py-1.5 sticky top-0 bg-background border-b">
              <Search className="w-4 h-4 text-muted-foreground mr-2" />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full text-sm bg-transparent outline-none placeholder:text-muted-foreground"
              />
            </div>

            {/* Options */}
            {filteredOptions.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">
                No results found
              </div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    onChange(opt.value);
                    setShowDropdown(false);
                    setSearch("");
                  }}
                  className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                    value === opt.value
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-muted/70 text-foreground"
                  }`}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterableDropdown;
